import React, { Fragment } from 'react';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';
import { MediumText } from 'spa/features/ui/EscrowTypography';
import { ACTION_REQUIRED_STATUSES } from 'spa/constants/TransactionConstants';
import { gettext } from '../../../../../utils/filters';

const ACTION_STATUS = {
  COMPLETE: 'COMPLETE',
  PENDING: 'PENDING',
  REQUIRED: 'REQUIRED',
};

const actionTypeToDescriptionMapping = {
  accept_return: gettext('Accept returned goods'),
  accept_shipment: gettext('Accept goods'),
  accept_transaction: gettext('Accept transaction'),
  account_info: gettext('Complete account profile'),
  additional_documents: gettext('Upload additional documents'),
  company_verification: gettext('Company verification'),
  disbursement_info: gettext('Disbursement details'),
  extended_verification: gettext('Photo verification'),
  funds: gettext('Complete payment'),
  goods_receipt: gettext('Confirm receipt of goods'),
  goods_return: gettext('Return goods'),
  goods_return_receipt: gettext('Confirm receipt of returned goods'),
  goods_shipping: gettext('Ship goods'),
  personal_verification: gettext('Individual verification'),
  sign_dnh_agreement: gettext('Sign DNH agreement'),
};

const accountVerificationActionTypes = ['personal_verification', 'company_verification'];

const documentTypeToDescriptionMapping = {
  "Party's Written Agreement": gettext('Upload written agreement'),
  Invoice: gettext('Upload purchase order/invoice'),
  OTHER: gettext('Upload additional documents'),
};

function mapActionTypeToDescription(actionType, transaction) {
  if (actionType === 'contact_party_for_return') {
    const contactDetails = transaction.rejectionContactDetails;
    if (contactDetails) {
      return gettext(
        'Contact %s to negotiate return of goods',
        contactDetails.role || 'other party'
      );
    }
    return actionTypeToDescriptionMapping.goods_return;
  }

  return actionTypeToDescriptionMapping[actionType] || actionType;
}

function isCompletedActionStatus(currentStatus) {
  return (
    !ACTION_REQUIRED_STATUSES.includes(currentStatus) && currentStatus !== ACTION_STATUS.PENDING
  );
}

function actionsToActionLines(actions, transaction, unmaskAccountVerification = false) {
  const actionLines = [];
  let accountVerificationAdded = false;

  for (const [actionType, action] of Object.entries(actions)) {
    if (accountVerificationActionTypes.includes(actionType) && !unmaskAccountVerification) {
      if (!accountVerificationAdded) {
        actionLines.push({
          key: 'account_verification',
          actionCurrentStatus: action.status,
          description: gettext('Account verification'),
        });
        accountVerificationAdded = true;
      }
    } else if (actionType === 'additional_documents') {
      const requiredDocuments = action.details.required_documents.map((doc) => ({
        key: `${actionType}--required--${doc}`,
        actionCurrentStatus: ACTION_STATUS.REQUIRED,
        description: documentTypeToDescriptionMapping[doc],
      }));

      const pendingDocuments = action.details.pending_documents.map((doc) => ({
        key: `${actionType}--pending--${doc}`,
        actionCurrentStatus: ACTION_STATUS.PENDING,
        description: documentTypeToDescriptionMapping[doc],
      }));

      actionLines.push(...requiredDocuments, ...pendingDocuments);
    } else {
      actionLines.push({
        key: actionType,
        actionCurrentStatus: action.status,
        description: mapActionTypeToDescription(actionType, transaction),
      });
    }
  }

  return actionLines;
}

function ActionLine({ actionCurrentStatus, description }) {
  const theme = useTheme();

  const leaderElement = isCompletedActionStatus(actionCurrentStatus) ? (
    <CheckCircleIcon sx={{ color: theme.palette.primary.main, top: '-2px' }} />
  ) : (
    <RadioButtonUncheckedIcon />
  );

  return (
    <Grid container spacing={1}>
      <Grid item>{leaderElement}</Grid>
      <Grid item>{description}</Grid>
    </Grid>
  );
}

function OutstandingActionsForParty({ actionLines, heading, isMobileViewport }) {
  const theme = useTheme();

  return (
    <Grid item sx={{ width: isMobileViewport ? '100%' : '50%' }}>
      <Paper
        variant="outlined"
        sx={{
          backgroundColor: theme.palette.mono.light,
          padding: '10px',
          height: '100%',
        }}
      >
        <Stack spacing={1}>
          <strong>{heading}</strong>
          {actionLines.length === 0 ? (
            <ActionLine
              key="completed"
              actionCurrentStatus={ACTION_STATUS.COMPLETE}
              description="Completed"
            />
          ) : (
            actionLines.map((actionLine) => (
              <ActionLine
                key={actionLine.key}
                actionCurrentStatus={actionLine.actionCurrentStatus}
                description={actionLine.description}
              />
            ))
          )}
        </Stack>
      </Paper>
    </Grid>
  );
}

function TransactionOutstandingActions({
  currentUserRole,
  isMobileViewport,
  otherPartyIsConfidential = true,
  transaction,
}) {
  const theme = useTheme();

  if (!transaction) {
    return <Fragment />;
  }

  const actions = transaction.actions_by_party;
  const buyerActionLines = actionsToActionLines(
    actions.BUYER,
    transaction,
    !otherPartyIsConfidential || currentUserRole === 'buyer'
  );
  const sellerActionLines = actionsToActionLines(
    actions.SELLER,
    transaction,
    !otherPartyIsConfidential || currentUserRole === 'seller'
  );

  const PARTIES_COPY_BY_ROLE = {
    buyer: 'you and the seller',
    seller: 'you and the buyer',
    broker: 'the buyer and the seller',
  };

  return (
    <Fragment>
      <div style={{ marginBottom: '1em' }}>
        <GppMaybeOutlinedIcon
          style={{
            color: theme.palette.error.contrastText,
            width: '1.5em',
            marginRight: '3px',
            marginBottom: '-5px',
            marginLeft: '-1px',
          }}
        />
        <MediumText
          sx={{ color: theme.palette.error.contrastText, display: 'inline', marginBottom: '10px' }}
        >
          Outstanding actions
        </MediumText>
      </div>
      <span>
        {gettext(
          'To secure the payment, %s still need to complete the following actions.',
          PARTIES_COPY_BY_ROLE[currentUserRole]
        )}
      </span>
      <Grid
        container
        direction={isMobileViewport ? 'column' : 'row'}
        spacing={1.5}
        columns={isMobileViewport ? 1 : 2}
        sx={{ width: '100%', marginTop: '8px' }}
      >
        <OutstandingActionsForParty
          actionLines={buyerActionLines}
          heading={currentUserRole === 'buyer' ? gettext('Your actions') : gettext('Buyer actions')}
          isMobileViewport={isMobileViewport}
        />
        <OutstandingActionsForParty
          actionLines={sellerActionLines}
          heading={
            currentUserRole === 'seller' ? gettext('Your actions') : gettext('Seller actions')
          }
          isMobileViewport={isMobileViewport}
        />
      </Grid>
    </Fragment>
  );
}

export default TransactionOutstandingActions;
